import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

// ── LeagueNav ──────────────────────────────────────────────────────────────────
const LeagueNav = () => {
  const { user, logout, isAdmin, isCoach, isPlayerParent, isPending, isTeamManager, isLeagueManager, linkedPlayerId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { to: '/teams',  label: 'Teams',  icon: 'ti-trophy' },
    ...(isPlayerParent && linkedPlayerId && !isPending
      ? [{ to: `/players/${linkedPlayerId}`, label: 'My Player', icon: 'ti-user' }]
      : []),
    ...((isAdmin || isCoach)       ? [{ to: '/coach',   label: 'Coach',   icon: 'ti-speakerphone' }] : []),
    ...((isAdmin || isTeamManager) ? [{ to: '/manager', label: 'Manager', icon: 'ti-clipboard-list' }] : []),
    ...((isAdmin || isTeamManager) ? [{ to: '/ai-tools',label: 'AI Tools',icon: 'ti-robot' }] : []),
    ...((isAdmin || isLeagueManager) ? [{ to: '/league-portal', label: 'League', icon: 'ti-building-stadium' }] : []),
    ...(isAdmin                    ? [{ to: '/admin',   label: 'Admin',   icon: 'ti-settings' }] : []),
  ];

  const isActive = (to: string) =>
    to === '/teams'
      ? location.pathname.startsWith('/teams') || location.pathname.startsWith('/players')
      : location.pathname.startsWith(to);

  const initials = user?.username?.slice(0, 2).toUpperCase() || '??';

  return (
    <nav className="league-nav">
      <div className="league-nav-inner">
        {/* Logo + name */}
        <Link to="/teams" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div className="league-mark">
            <i className="ti ti-broadcast" style={{ fontSize: 18 }} />
          </div>
          <div className="league-name-block">
            <div className="ln">Slow Rib</div>
            <div className="ls">Sports Streaming</div>
          </div>
        </Link>

        {/* Desktop tabs */}
        <div className="league-tabs">
          {tabs.map(tab => (
            <Link
              key={tab.to}
              to={tab.to}
              className={`league-tab${isActive(tab.to) ? ' active' : ''}`}
            >
              <i className={`ti ${tab.icon}`} style={{ fontSize: 16, marginRight: 4 }} />
              {tab.label}
            </Link>
          ))}
        </div>

        {/* Actions */}
        <div className="league-nav-actions">
          <Link to="/account">
            <div className="user-avatar" title={user?.username}>
              {initials}
            </div>
          </Link>
        </div>
      </div>
    </nav>
  );
};

// ── MobileTabbar ───────────────────────────────────────────────────────────────
const MobileTabbar = () => {
  const { isAdmin, isCoach, isPlayerParent, isPending, isTeamManager, isLeagueManager, linkedPlayerId } = useAuth();
  const location = useLocation();

  const tabs = [
    { to: '/teams',   label: 'Teams',  icon: 'ti-trophy' },
    ...(isPlayerParent && linkedPlayerId && !isPending
      ? [{ to: `/players/${linkedPlayerId}`, label: 'My Player', icon: 'ti-user' }]
      : []),
    ...((isAdmin || isCoach)
      ? [{ to: '/coach', label: 'Coach', icon: 'ti-speakerphone' }] : []),
    ...((isAdmin || isTeamManager)
      ? [{ to: '/manager', label: 'Manager', icon: 'ti-clipboard-list' }] : []),
    ...((isAdmin || isLeagueManager)
      ? [{ to: '/league-portal', label: 'League', icon: 'ti-building-stadium' }] : []),
    ...(isAdmin
      ? [{ to: '/admin', label: 'Admin', icon: 'ti-settings' }] : []),
    { to: '/account', label: 'Account', icon: 'ti-user-circle' },
  ];

  const isActive = (to: string) =>
    to === '/teams'
      ? location.pathname.startsWith('/teams') || location.pathname.startsWith('/players')
      : location.pathname.startsWith(to);

  return (
    <nav className="mobile-tabbar">
      {tabs.map(tab => (
        <Link
          key={tab.to}
          to={tab.to}
          className={`mobile-tab${isActive(tab.to) ? ' active' : ''}`}
        >
          <i className={`ti ${tab.icon}`} />
          <span>{tab.label}</span>
        </Link>
      ))}
    </nav>
  );
};

// ── Live game banner ───────────────────────────────────────────────────────────
const LiveBanner = () => {
  const location = useLocation();
  const [liveGame, setLiveGame] = useState<any>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    const check = async () => {
      try {
        const r = await api.get('/games');
        setLiveGame(r.data.find((g: any) => g.status === 'live') ?? null);
      } catch {}
    };
    check();
    const t = setInterval(check, 30000);

    const SERVER_URL = process.env.REACT_APP_SERVER_URL ?? '';
    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });
    socket.on('game_went_live', check);
    return () => { clearInterval(t); socket.disconnect(); };
  }, []);

  if (!liveGame || liveGame.id === dismissed || location.pathname === `/watch/${liveGame?.id}`) return null;

  return (
    <div className="live-banner">
      <div className="live-dot" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--live)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1px' }}>
          Live Now
        </div>
        <div style={{ fontSize: '14px', fontWeight: 700, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {liveGame.title}
        </div>
      </div>
      <Link
        to={`/watch/${liveGame.id}`}
        style={{ flexShrink: 0, background: 'var(--live)', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '13px', textDecoration: 'none', whiteSpace: 'nowrap' }}
      >
        Watch →
      </Link>
      <button
        onClick={() => setDismissed(liveGame.id)}
        style={{ flexShrink: 0, background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: '4px 8px', fontSize: '18px', lineHeight: 1 }}
      >
        ✕
      </button>
    </div>
  );
};

// ── Status banners (pending / rejected) ───────────────────────────────────────
const StatusBanner = () => {
  const { isPending, isRejected, isPendingTeamManager, requestedRole } = useAuth();

  if (isPendingTeamManager && !isPending) return (
    <div style={{ background: 'var(--win-bg)', borderBottom: '1px solid #A7D9BA', padding: '10px 24px', fontSize: '13px', color: 'var(--win)', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', textAlign: 'center' }}>
      🗂️ Your Team Manager request is awaiting admin approval.
    </div>
  );

  if (isPending) return (
    <div style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A', padding: '10px 24px', fontSize: '13px', color: '#92400E', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', textAlign: 'center' }}>
      ⏳ Your {requestedRole === 'player_parent' ? 'Player/Parent' : 'Coach'} account is awaiting admin approval. You have Fan access until then.
    </div>
  );

  if (isRejected) return (
    <div style={{ background: 'var(--loss-bg)', borderBottom: '1px solid #F5A3AB', padding: '10px 24px', fontSize: '13px', color: 'var(--loss)', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', textAlign: 'center' }}>
      ❌ Your account request was declined. Please contact the league administrator.
    </div>
  );

  return null;
};

// ── Layout ─────────────────────────────────────────────────────────────────────
const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <LeagueNav />
      <StatusBanner />

      <main className="app-main" style={{ flex: 1, paddingTop: 24, paddingBottom: 48 }}>
        {children}
      </main>

      <footer style={{
        background: 'var(--ink-1)', borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: '12px',
      }}>
        <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>Slow Rib Sports Streaming</span>
        {' '}&copy; {new Date().getFullYear()}
        <span style={{ margin: '0 8px', opacity: 0.3 }}>|</span>
        Youth Baseball · Softball · Soccer
        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center', gap: '16px' }}>
          <Link to="/terms"   style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px' }}>Terms of Service</Link>
          <Link to="/privacy" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px' }}>Privacy Policy</Link>
        </div>
      </footer>

      <MobileTabbar />
      <LiveBanner />
    </div>
  );
};

export default Layout;
